import type { panelIds } from '@game/constants';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import type { PersistentSettingsState } from './persistentState';
import { persistentSettingsDefault } from './persistentState';

interface PersistentState extends PersistentSettingsState {
  expectSignIn: boolean;
  /** UID of the user who owns this persisted state. Used to prevent cross-user rehydration across tabs. */
  uid: string;
  inputHistory: string[];
  activeMissionsCount: number;
  chatChannel: string;
  chatChannelsLastReadTime: Record<string, number>;
  pinnedMissionRecordIds: string[];
  setPinnedMissions: (pinnedMissionRecordIds: string[]) => void;
  addPinnedMissionRecordId: (missionRecordId: string) => void;
  removePinnedMissionRecordId: (missionRecordId: string) => void;
  setVolume: (volume: number) => void;
  setMusicLoopDelay: (delayMs: number) => void;
  setExpectSignIn: (expectSignIn: boolean) => void;
  setPanelOrder: (panelId: keyof typeof panelIds) => void;
  toggleChatPanel: () => void;
  displayChatPanel: () => void;
  changeChatChannel: (channel: string) => void;
  toggleModeratorPanel: () => void;
}

function getReorderedPanelOrder(panelOrder: string[], panelId: string): string[] {
  return [panelId, ...panelOrder.filter((p) => p !== panelId)];
}

export const persistentStore = create<PersistentState>()(
  persist(
    (set, get) => ({
      ...persistentSettingsDefault,
      expectSignIn: false,
      uid: '',
      inputHistory: [],
      activeMissionsCount: 0,
      chatChannel: 'main',
      chatChannelsLastReadTime: {},
      pinnedMissionRecordIds: [],
      setVolume: (volume: number): void => {
        set({ volume });
      },
      setMusicLoopDelay: (delayMs: number): void => {
        set({ musicLoopDelay: delayMs });
      },
      setExpectSignIn: (expectSignIn: boolean): void => {
        set({ expectSignIn });
      },
      toggleChatPanel: (): void => {
        set((state) => ({
          ...state,
          showChatPanel: !persistentStore.getState().showChatPanel,
          panelOrder: getReorderedPanelOrder(get().panelOrder, 'chat'),
        }));
      },
      toggleModeratorPanel: (): void => {
        set((state) => ({
          ...state,
          showModeratorPanel: !persistentStore.getState().showModeratorPanel,
        }));
      },
      displayChatPanel: (): void => {
        set((state) => ({
          ...state,
          showChatPanel: true,
          panelOrder: getReorderedPanelOrder(get().panelOrder, 'chat'),
        }));
      },
      setPanelOrder: (panelId): void => {
        set((state) => ({
          ...state,
          panelOrder: [panelId, ...get().panelOrder.filter((p) => p !== panelId)],
        }));
      },
      changeChatChannel: (channel): void => {
        set((state) => ({
          ...state,
          chatChannel: channel,
          chatChannelsLastReadTime: {
            ...state.chatChannelsLastReadTime,
            [channel]: Date.now(),
          },
        }));
      },
      addPinnedMissionRecordId: (missionRecordId): void => {
        if (get().pinnedMissionRecordIds.includes(missionRecordId)) {
          return;
        }

        set((state) => ({
          ...state,
          pinnedMissionRecordIds: [...state.pinnedMissionRecordIds, missionRecordId],
        }));
      },
      setPinnedMissions: (pinnedMissionRecordIds): void => {
        set((state) => ({
          ...state,
          pinnedMissionRecordIds,
        }));
      },
      removePinnedMissionRecordId: (missionRecordId): void => {
        set((state) => ({
          ...state,
          pinnedMissionRecordIds: state.pinnedMissionRecordIds.filter((id) => id !== missionRecordId),
        }));
      },
    }),
    {
      name: 'bw', // unique name,
      version: 2,
    },
  ),
);
